import React from 'react';
import { ProgressBar } from 'primereact/progressbar';
import { Button } from 'primereact/button';

const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return null;
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

const DismissButton = ({ id, remove }) =>
  remove ? (
    <Button
      icon="fas fa-times"
      className="p-button-text p-button-sm"
      style={{ color: '#999', padding: '2px', minWidth: 'unset', flexShrink: 0 }}
      onClick={() => remove(id)}
      tooltip="Dismiss"
      tooltipOptions={{ position: 'top' }}
    />
  ) : null;

const PreparingPhase = ({ label, message, id, remove }) => (
  <>
    <div className="d-flex justify-content-between align-items-center mb-2">
      <div className="d-flex align-items-center flex-1" style={{ marginRight: '8px', minWidth: 0 }}>
        <p className="mb-0 text-truncate" style={{ fontWeight: '600', fontSize: '13px' }} title={label}>
          {label}
        </p>
      </div>
      <DismissButton id={id} remove={remove} />
    </div>
    <p className="text-hint mb-2" style={{ fontSize: '12px' }}>
      {message || 'Preparing...'}
    </p>
    <ProgressBar mode="indeterminate" style={{ height: '4px' }} color="var(--success)" />
  </>
);

const DownloadingPhase = ({ label, fileName, progress, loaded, total, error, id, remove }) => {
  const loadedStr = formatBytes(loaded);
  const totalStr = formatBytes(total);
  const showBytes = loadedStr && totalStr;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center flex-1" style={{ gap: '8px', marginRight: '8px', minWidth: 0 }}>
          <i className="fas fa-download" style={{ fontSize: '13px', color: 'var(--primary-main)', flexShrink: 0 }} />
          <p className="mb-0 text-truncate" style={{ fontWeight: '600', fontSize: '13px' }} title={label || fileName}>
            {label || fileName}
          </p>
        </div>
        <DismissButton id={id} remove={remove} />
      </div>
      {fileName && label && fileName !== label && (
        <p className="text-hint mb-1" style={{ fontSize: '11px', wordBreak: 'break-word' }}>
          {fileName}
        </p>
      )}
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="text-hint" style={{ fontSize: '11px' }}>
          {showBytes ? `${loadedStr} / ${totalStr}` : 'Downloading...'}
        </span>
        <span style={{ fontSize: '11px', fontWeight: '600', color: '#555' }}>{Math.round(progress)}%</span>
      </div>
      <ProgressBar value={progress} style={{ height: '6px' }} showValue={false} color="var(--success)" />
      {error && (
        <p className="mb-0 mt-1" style={{ fontSize: '11px', color: 'var(--sec-red)' }}>
          Download failed
        </p>
      )}
    </>
  );
};

const DonePhase = ({ successHeadline, fileName, totalFiles, id, remove }) => (
  <>
    <div className="d-flex justify-content-between align-items-start">
      <div className="d-flex align-items-start flex-1" style={{ gap: '10px', marginRight: '8px', minWidth: 0 }}>
        <i
          className="fas fa-check-circle dl-tick-icon"
          style={{ fontSize: '20px', color: 'var(--success)', flexShrink: 0, marginTop: '1px' }}
        />
        <div className="flex-1" style={{ minWidth: 0 }}>
          <p className="mb-1" style={{ fontWeight: '600', fontSize: '13px' }}>
            {successHeadline || 'Download complete'}
          </p>
          {fileName && (
            <p className="text-hint mb-0" style={{ fontSize: '11px', wordBreak: 'break-word' }}>
              {fileName}
            </p>
          )}
          {totalFiles != null && totalFiles !== '' && (
            <p className="text-hint mb-0" style={{ fontSize: '11px' }}>
              Total files: {totalFiles}
            </p>
          )}
        </div>
      </div>
      <DismissButton id={id} remove={remove} />
    </div>
  </>
);

const FailedPhase = ({ label, fileName, error, id, remove }) => (
  <div className="d-flex justify-content-between align-items-start">
    <div className="d-flex align-items-start flex-1" style={{ gap: '10px', marginRight: '8px', minWidth: 0 }}>
      <i className="fas fa-exclamation-circle" style={{ fontSize: '18px', color: 'var(--sec-red)', flexShrink: 0, marginTop: '1px' }} />
      <div style={{ minWidth: 0 }}>
        <p className="mb-1 text-truncate" style={{ fontWeight: '600', fontSize: '13px' }} title={label || fileName}>
          {label || fileName || 'Download failed'}
        </p>
        <p className="mb-0" style={{ fontSize: '11px', color: 'var(--sec-red)' }}>
          {typeof error === 'string' ? error : error?.message || 'Failed to download. Please try again.'}
        </p>
      </div>
    </div>
    <DismissButton id={id} remove={remove} />
  </div>
);

/**
 * Unified download card shown in the DownloadTray.
 * Renders one of four phases: 'preparing' | 'downloading' | 'done' | 'failed'
 *
 * @param {string}  phase           - Current phase of the download
 * @param {string}  label           - Primary display name (folder name or file name)
 * @param {string}  fileName        - Actual file name (zip name, pdf name, etc.)
 * @param {number}  progress        - 0–100 percent complete
 * @param {number}  loaded          - Bytes downloaded so far
 * @param {number}  total           - Total bytes to download
 * @param {string}  message         - Status hint shown under the label during 'preparing'
 * @param {*}       error           - Error string or Error object
 * @param {string}  successHeadline - Headline text shown in the 'done' phase
 * @param {number}  totalFiles      - Optional file count shown in the 'done' phase
 * @param {string}  id              - Download entry ID
 * @param {Function} remove         - Callback to manually dismiss the card
 * @param {boolean} isDismissing    - When true, applies fade-out transition before removal
 */
const FloatingProgressBar = ({
  phase,
  label,
  fileName,
  progress = 0,
  loaded,
  total,
  message,
  error,
  successHeadline,
  totalFiles,
  id,
  remove,
  isDismissing,
}) => {
  const cardStyle = {
    transition: 'opacity 300ms ease, transform 300ms ease',
    opacity: isDismissing ? 0 : 1,
    transform: isDismissing ? 'translateY(8px)' : 'translateY(0)',
  };

  const renderPhase = () => {
    switch (phase) {
      case 'preparing':
        return <PreparingPhase label={label} message={message} id={id} remove={remove} />;
      case 'downloading':
        return (
          <DownloadingPhase
            label={label}
            fileName={fileName}
            progress={progress}
            loaded={loaded}
            total={total}
            error={error}
            id={id}
            remove={remove}
          />
        );
      case 'done':
        return <DonePhase successHeadline={successHeadline} fileName={fileName} totalFiles={totalFiles} id={id} remove={remove} />;
      case 'failed':
        return <FailedPhase label={label} fileName={fileName} error={error} id={id} remove={remove} />;
      default:
        return null;
    }
  };

  return (
    <div className="inner-card p-3 w-100 mb-2" style={cardStyle}>
      {renderPhase()}
    </div>
  );
};

export default FloatingProgressBar;
